import { registerSW } from 'virtual:pwa-register';

/**
 * Registro do service worker, uma vez só, no arranque.
 *
 * Isto é separado da UI de propósito. A primeira versão chamava `registerSW`
 * dentro do componente do aviso, que só monta para usuário autenticado: o
 * resultado era que ninguém deslogado registrava nada, e a verificação no build
 * servido devolveu zero service workers. Registrar é infraestrutura e acontece
 * sempre; avisar é interface e acontece quando há o que dizer.
 *
 * O módulo guarda o estado porque `registerSW` só pode ser chamado uma vez por
 * página: chamadas repetidas criam registros concorrentes e o callback de
 * atualização passa a disparar mais de uma vez para o mesmo evento.
 */

type Ouvinte = () => void;

let precisaAtualizar = false;
let atualizar: ((recarregar?: boolean) => Promise<void>) | null = null;
let registrado = false;
const ouvintes = new Set<Ouvinte>();

export function registrarServiceWorker(): void {
  if (registrado) return;
  registrado = true;

  atualizar = registerSW({
    onNeedRefresh() {
      precisaAtualizar = true;
      ouvintes.forEach(fn => fn());
    },
  });
}

export function assinarAtualizacao(fn: Ouvinte): () => void {
  ouvintes.add(fn);
  return () => { ouvintes.delete(fn); };
}

export function haAtualizacao(): boolean {
  return precisaAtualizar;
}

/** Recarrega no código novo. O usuário decide quando; ver PwaPrompts. */
export function aplicarAtualizacao(): void {
  void atualizar?.(true);
}

/**
 * Puxa o shell novo AGORA, sem esperar o usuário aceitar o aviso.
 *
 * Existe por causa de uma janela real de app quebrado depois de todo deploy:
 * o `navigateFallback` do Workbox serve o `index.html` PRECACHEADO, e o
 * precache guarda só o shell. Os chunks de módulo (Gantt, Kanban, Portfólio...)
 * ficam de fora de propósito, então eles vão à rede. Depois de publicar, os
 * arquivos de hash antigo não existem mais no servidor: o shell velho continua
 * abrindo, a sidebar aparece inteira, e o primeiro clique em qualquer módulo
 * responde 404. Com `registerType: 'prompt'`, isso só se cura quando o usuário
 * aceita o aviso, ou seja, pode nunca.
 *
 * Recarregar sozinho NÃO resolve: o service worker responderia a navegação com
 * o mesmo `index.html` de cache. Por isso a ordem é reconferir o `sw.js`,
 * ativar o worker que estiver esperando e só então recarregar.
 */
export async function puxarShellNovo(): Promise<void> {
  try {
    const registro = await navigator.serviceWorker?.getRegistration();
    // `update()` força a reconsulta do sw.js; sem ele o worker novo pode nem ter
    // sido baixado ainda, e não haveria o que ativar.
    await registro?.update();

    if (registro?.waiting && atualizar) {
      void atualizar(true); // ativa o worker novo E recarrega
      return;
    }
  } catch {
    /* sem service worker, ou browser negou: cai no reload puro abaixo */
  }

  window.location.reload();
}
